import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, Columns3, EyeOff, Filter, ListTree, MoveHorizontal, Pin, PinOff, Plus } from 'lucide-react'
import { Menu } from '../controls/Menu'
import s from './GridColumnMenu.module.css'

/**
 * A COLUMN'S OPTIONS — the one place sort, filter, group, hide and the column
 * chooser live (#2375, ADR F-84).
 *
 * The approved unified grid removes the toolbar's Filter / Columns / Sort
 * band: "column operations live in the column header". This is the menu the
 * header's always-visible ⋮ opens, and it is the SAME kit `Menu` (Radix) the
 * view-settings ⋮ uses — roving arrows, Home/End, type-ahead, Escape and
 * outside click are Radix's, not a second hand-rolled surface.
 *
 * ── Only verbs the host can perform ────────────────────────────────────────
 * Every item is a prop, and an absent prop is an absent item (anatomy §4: "a
 * menu item that does nothing is worse than none"). That is how a grid whose
 * adapter cannot group exactly never shows "Group by" at all, rather than
 * showing it disabled with an excuse (ADR F-84 §2). "Pin column left" and
 * "Autosize" drive DataGrid's own `pinnedColumns` and measured widths (#2375).
 *
 * ── Focus ──────────────────────────────────────────────────────────────────
 * The menu is ANCHORED to the header's ⋮ (`Menu`'s anchor mode). Radix would
 * return focus to its invisible stand-in trigger, so `onCloseAutoFocus` is
 * prevented here and `DataGrid`'s own restoration puts focus back on the
 * `[data-colmenu]` button — the mechanism that survives the grid remounting
 * after a filter changes the query.
 */
export interface GridColumnMenuProps {
  /** The ⋮ that opened it (from `DataGrid`'s `renderColumnMenu`). */
  anchor: HTMLElement
  /** Closes the menu; `DataGrid` then restores focus to the ⋮. */
  onClose: () => void
  columnKey: string
  /** The column's human label, used as the menu's heading. */
  label: string
  /** Absent ⇒ the column is not sortable here. */
  sort?: {
    direction: 'asc' | 'desc' | null
    onSort: (direction: 'asc' | 'desc') => void
    /** Absent ⇒ the host keeps one sort key. */
    onAddSort?: () => void
  }
  /** Opens the column's value editor (`ColumnFilterEditor`). */
  onFilter?: () => void
  /** Absent ⇒ this adapter cannot group by this column exactly. */
  group?: { active: boolean; onToggle: () => void }
  /**
   * "Pin column left" / "Unpin column" (#2375 §6.3) over the host's
   * `pinnedColumns` state, which `DataGrid` draws. Absent ⇒ no item.
   */
  pin?: { pinned: boolean; onToggle: () => void }
  /** "Autosize" — `DataGrid` hands the host this from `renderColumnMenu`. Absent ⇒ no item. */
  onAutosize?: () => void
  onHide?: () => void
  /** The show / hide / add / reorder list — the one way back to a hidden column. */
  onColumns?: () => void
  /** Host-specific items, drawn before "Columns…". */
  children?: ReactNode
}

export function GridColumnMenu({
  anchor, onClose, columnKey, label, sort, onFilter, group, pin, onAutosize, onHide, onColumns, children,
}: GridColumnMenuProps) {
  const act = (fn: () => void) => () => { fn(); onClose() }
  const sections: ReactNode[] = []
  if (sort) {
    sections.push(
      <div key="sort">
        <Menu.RadioGroup value={sort.direction ?? ''} onValueChange={(value) => {
          if (value === 'asc' || value === 'desc') { sort.onSort(value); onClose() }
        }}>
          <Menu.RadioItem value="asc" testId={`grid-col-${columnKey}-sort-asc`}>
            <ArrowUp size={14} aria-hidden="true" /> Sort ascending
          </Menu.RadioItem>
          <Menu.RadioItem value="desc" testId={`grid-col-${columnKey}-sort-desc`}>
            <ArrowDown size={14} aria-hidden="true" /> Sort descending
          </Menu.RadioItem>
        </Menu.RadioGroup>
        {sort.onAddSort && (
          <Menu.Item onSelect={act(sort.onAddSort)} testId={`grid-col-${columnKey}-sort-add`}>
            <Plus size={14} aria-hidden="true" /> Add to sort
            <kbd className={s.hint}>⇧ click</kbd>
          </Menu.Item>
        )}
      </div>,
    )
  }
  if (onFilter || group) {
    sections.push(
      <div key="shape">
        {onFilter && (
          <Menu.Item onSelect={act(onFilter)} testId={`grid-col-${columnKey}-filter`}>
            <Filter size={14} aria-hidden="true" /> Filter…
          </Menu.Item>
        )}
        {group && (
          <Menu.CheckItem checked={group.active} testId={`grid-col-${columnKey}-group`}
            onSelect={() => { group.onToggle(); onClose() }}>
            <ListTree size={14} aria-hidden="true" /> Group by {label}
          </Menu.CheckItem>
        )}
      </div>,
    )
  }
  /* The layout section, in the approved order: Pin column left · Autosize ·
     Hide column. Each item is present only when the host wires the state it
     changes — never an inert entry. */
  if (pin || onAutosize || onHide) {
    sections.push(
      <div key="layout">
        {pin && (
          <Menu.Item onSelect={act(pin.onToggle)} testId={`grid-col-${columnKey}-pin`}>
            {pin.pinned
              ? <><PinOff size={14} aria-hidden="true" /> Unpin column</>
              : <><Pin size={14} aria-hidden="true" /> Pin column left</>}
          </Menu.Item>
        )}
        {onAutosize && (
          <Menu.Item onSelect={act(onAutosize)} testId={`grid-col-${columnKey}-autosize`}>
            <MoveHorizontal size={14} aria-hidden="true" /> Autosize
          </Menu.Item>
        )}
        {onHide && (
          <Menu.Item onSelect={act(onHide)} testId={`grid-col-${columnKey}-hide`}>
            <EyeOff size={14} aria-hidden="true" /> Hide column
          </Menu.Item>
        )}
      </div>,
    )
  }
  if (children || onColumns) {
    sections.push(
      <div key="columns">
        {children}
        {onColumns && (
          <Menu.Item onSelect={act(onColumns)} testId={`grid-col-${columnKey}-columns`}>
            <Columns3 size={14} aria-hidden="true" /> Columns…
          </Menu.Item>
        )}
      </div>,
    )
  }
  return (
    <Menu anchor={anchor} open align="end" contentTestId={`grid-col-${columnKey}-menu-list`}
      onOpenChange={(open) => { if (!open) onClose() }}
      onCloseAutoFocus={(event) => event.preventDefault()}>
      <Menu.Label>{label}</Menu.Label>
      {sections.map((section, index) => (
        <div key={index}>
          {index > 0 && <Menu.Separator />}
          {section}
        </div>
      ))}
    </Menu>
  )
}
